import React, { useEffect, useRef } from "react";
import { m, useMotionValueEvent } from "framer-motion";
import { BOARD, SECTIONS } from "../../data/board";
import { applyBoardZoom } from "../../utils/mapCamera";
import { useCamera } from "./CameraProvider";
import { useBoardGeometry } from "./BoardGeometry";
import { useCameraGestures } from "../../hooks/useCameraGestures";
import ConnectionLayer from "./ConnectionLayer";
import BoardSection from "./BoardSection";
import BoardScatter from "./BoardScatter";
import BoardWatermarks from "./BoardWatermarks";
import HeroSection from "../../pages/investigation/HeroSection";
import ProjectsSection from "../../pages/investigation/ProjectsSection";
import ExperienceSection from "../../pages/investigation/ExperienceSection";
import SkillsSection from "../../pages/investigation/SkillsSection";
import LeadershipSection from "../../pages/investigation/LeadershipSection";
import EducationSection from "../../pages/investigation/EducationSection";
import ContactSection from "../../pages/investigation/ContactSection";
import PortraitsSection from "../../pages/investigation/PortraitsSection";

const ZOOM_SETTLE_MS = 100;

export const InvestigationBoard = () => {
  const viewportRef = useRef(null);
  const boardRef = useRef(null);
  const didCenter = useRef(false);
  const zoomSettleRef = useRef(0);
  const { x, y, scale, vw, vh, cssZoom, clampCurrent, resetView, flyToBoard } = useCamera();
  const { registerBoard } = useBoardGeometry();
  useCameraGestures(viewportRef);

  useEffect(() => {
    registerBoard(boardRef.current);
    return () => registerBoard(null);
  }, [registerBoard]);

  // Tabbing to a card that is off screen brings the camera to it.
  const followFocus = (event) => {
    const target = event.target;
    if (!target.matches?.(":focus-visible")) return;
    const rect = target.getBoundingClientRect();
    const view = viewportRef.current.getBoundingClientRect();
    const margin = 72;
    const inView =
      rect.left >= view.left + margin &&
      rect.right <= view.right - margin &&
      rect.top >= view.top + margin &&
      rect.bottom <= view.bottom - margin;
    if (inView) return;
    const zoom = scale.get();
    flyToBoard(
      (rect.left + rect.width / 2 - view.left - x.get()) / zoom,
      (rect.top + rect.height / 2 - view.top - y.get()) / zoom,
      zoom,
      true
    );
  };

  useEffect(() => {
    const node = viewportRef.current;
    if (!node || typeof ResizeObserver === "undefined") {
      vw.set(window.innerWidth);
      vh.set(window.innerHeight);
      resetView(false);
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      vw.set(rect.width);
      vh.set(rect.height);
      if (!didCenter.current) {
        didCenter.current = true;
        resetView(false);
      } else {
        clampCurrent();
      }
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [clampCurrent, resetView, vh, vw]);

  useEffect(() => {
    applyBoardZoom(boardRef.current, scale.get(), cssZoom);
  }, [cssZoom, scale]);

  // Mid-zoom, scale with a transform (repaint only); CSS zoom re-lays out the whole board every frame.
  // Once the zoom settles, switch back to CSS zoom so text is laid out crisp at the final size.
  // A one-off step (buttons, keys, one wheel tick) goes straight to CSS zoom, so it never swaps text rendering.
  useMotionValueEvent(scale, "change", (value) => {
    const continuing = zoomSettleRef.current !== 0;
    applyBoardZoom(boardRef.current, value, continuing ? false : cssZoom);
    window.clearTimeout(zoomSettleRef.current);
    zoomSettleRef.current = window.setTimeout(() => {
      zoomSettleRef.current = 0;
      if (continuing) applyBoardZoom(boardRef.current, scale.get(), cssZoom);
    }, ZOOM_SETTLE_MS);
  });

  useEffect(() => () => window.clearTimeout(zoomSettleRef.current), []);

  return (
    <div ref={viewportRef} className="camera-viewport" onFocus={followFocus}>
      <m.div
        className="camera-pan"
        style={{ x, y }}
        transformTemplate={({ x: tx, y: ty }) => `translate3d(${tx ?? 0}, ${ty ?? 0}, 0)`}
      >
        <div
          ref={boardRef}
          className="investigation-board"
          style={{
            width: BOARD.width,
            height: BOARD.height,
          }}
        >
          <div className="board-layer board-layer-bg" />
          <BoardWatermarks />

          <div className="board-layer board-layer-strings">
            <ConnectionLayer />
          </div>

          <div className="board-layer board-layer-docs">
            <BoardSection section={SECTIONS.overview}>
              <HeroSection />
            </BoardSection>
            <BoardSection section={SECTIONS.projects}>
              <ProjectsSection />
            </BoardSection>
            <BoardSection section={SECTIONS.experience}>
              <ExperienceSection />
            </BoardSection>
            <BoardSection section={SECTIONS.skills}>
              <SkillsSection />
            </BoardSection>
            <BoardSection section={SECTIONS.education}>
              <EducationSection />
            </BoardSection>
            <BoardSection section={SECTIONS.portraits}>
              <PortraitsSection />
            </BoardSection>
            <BoardSection section={SECTIONS.leadership}>
              <LeadershipSection />
            </BoardSection>
            <BoardSection section={SECTIONS.contact}>
              <ContactSection />
            </BoardSection>
          </div>

          <div className="board-layer board-layer-notes">
            <BoardScatter />
          </div>
        </div>
      </m.div>
      <div className="camera-vignette" />
    </div>
  );
};

export default InvestigationBoard;
